import { $, $$, on, setAttr, removeAttr } from "../../utils/dom";

// Keep in lockstep with the `@media (max-width: 768px)` rules in
// falckChatBlock.css: JS owns the takeover behaviour, CSS owns the layout.
const MOBILE_QUERY = "(max-width: 768px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const CONVERSATION = "-conversation";
const COLLAPSED = "-mobile-collapsed";
const TRANSITIONING = "-transitioning";
const PAGE_LOCK = "falck-chat-locked";

// Tail after the desktop expand transition before scrolling the input into view.
const TRANSITION_TAIL_MS = 300;

type ChatMessage = {
  type?: string;
  height?: number;
  block?: ScrollLogicalPosition;
  state?: string;
  initial?: boolean;
};

const focusEl = (el: HTMLElement | null): void => {
  if (!el) {
    return;
  }
  try {
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
};

function falckChatBlock(wrapper: HTMLElement): void {
  const iframe = $(".m-falckChatBlock__iframe", wrapper) as HTMLIFrameElement | null;
  if (!iframe) {
    return;
  }

  let trustedOrigin: string;
  try {
    trustedOrigin = new URL(iframe.src, window.location.href).origin;
  } catch {
    return;
  }

  const closeBtn = $(".m-falckChatBlock__close", wrapper);
  const openBtn = $(".m-falckChatBlock__open", wrapper);
  const mobileQuery = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
  const isMobile = (): boolean => !!(mobileQuery && mobileQuery.matches);
  const vv = window.visualViewport || null;
  const token = wrapper.getAttribute("data-falck-chat-token") || "";

  const postToChat = (message: unknown): void => {
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(message, trustedOrigin);
    }
  };

  // Forward the CMS height to CSS via CSSOM (CSP-safe); CSS falls back to 54rem.
  const cmsHeight = wrapper.getAttribute("data-falck-chat-expanded-height");
  if (cmsHeight && /^\d{1,5}$/.test(cmsHeight) && parseInt(cmsHeight, 10) > 0) {
    wrapper.style.setProperty("--falck-chat-expanded-height-cms", `${cmsHeight}px`);
  }

  // Tell the chat to blur its message list (not the composer) while collapsed,
  // as a "tap to open" preview hint.
  const setChildPreview = (enabled: boolean): void =>
    postToChat({ type: "falck-chat:preview", on: enabled });

  let lockedScrollY = 0;
  const lockPage = (): void => {
    const root = document.documentElement;
    if (root.classList.contains(PAGE_LOCK)) {
      return;
    }
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    root.classList.add(PAGE_LOCK);
    document.body.style.top = `${-lockedScrollY}px`;
  };
  const unlockPage = (): void => {
    const root = document.documentElement;
    if (!root.classList.contains(PAGE_LOCK)) {
      return;
    }
    root.classList.remove(PAGE_LOCK);
    document.body.style.top = "";
    window.scrollTo(0, lockedScrollY);
  };

  const showCloseBtn = (show: boolean): void => {
    if (!closeBtn) {
      return;
    }
    if (show) {
      removeAttr(closeBtn, "hidden");
    } else {
      setAttr(closeBtn, "hidden");
    }
  };

  // Mark the wrapper as a modal so screen readers stay within it (incl. close).
  const setDialogAttrs = (enabled: boolean): void => {
    if (enabled) {
      setAttr(wrapper, "role", "dialog");
      setAttr(wrapper, "aria-modal", "true");
      const label = iframe.getAttribute("title");
      if (label) {
        setAttr(wrapper, "aria-label", label);
      }
    } else {
      removeAttr(wrapper, "role");
      removeAttr(wrapper, "aria-modal");
      removeAttr(wrapper, "aria-label");
    }
  };

  const isMobileFullscreen = (): boolean =>
    isMobile() &&
    wrapper.classList.contains(CONVERSATION) &&
    !wrapper.classList.contains(COLLAPSED);

  // Size to the visual viewport, not 100dvh: on iOS a fixed 100dvh element sits
  // behind the keyboard, hiding the input/newest message.
  const applyFullscreenViewport = (): void => {
    if (!vv || !isMobileFullscreen()) {
      return;
    }
    iframe.style.top = `${vv.offsetTop}px`;
    iframe.style.left = `${vv.offsetLeft}px`;
    iframe.style.width = `${vv.width}px`;
    iframe.style.height = `${vv.height}px`;
    iframe.style.right = "auto";
    iframe.style.bottom = "auto";
  };
  const clearFullscreenViewport = (): void => {
    iframe.style.top = "";
    iframe.style.left = "";
    iframe.style.width = "";
    iframe.style.height = "";
    iframe.style.right = "";
    iframe.style.bottom = "";
  };

  let vvBound = false;
  const bindViewport = (): void => {
    if (!vv || vvBound) {
      return;
    }
    vvBound = true;
    vv.addEventListener("resize", applyFullscreenViewport);
    vv.addEventListener("scroll", applyFullscreenViewport);
  };
  const unbindViewport = (): void => {
    if (!vv || !vvBound) {
      return;
    }
    vvBound = false;
    vv.removeEventListener("resize", applyFullscreenViewport);
    vv.removeEventListener("scroll", applyFullscreenViewport);
  };

  // Tear down the full-screen takeover. Callers decide the collapsed/preview
  // state separately.
  const teardownFullscreen = (): void => {
    unbindViewport();
    clearFullscreenViewport();
    unlockPage();
    showCloseBtn(false);
    setDialogAttrs(false);
  };

  const enterFullscreen = (focusClose: boolean = false): void => {
    if (!isMobile()) {
      return;
    }
    wrapper.classList.remove(COLLAPSED);
    lockPage();
    showCloseBtn(true);
    setDialogAttrs(true);
    setChildPreview(false);
    bindViewport();
    applyFullscreenViewport();
    // Focus close only when opened via the badge; send paths keep composer
    // focus so the keyboard stays put.
    if (focusClose) {
      focusEl(closeBtn);
    }
  };

  const collapse = (returnFocus: boolean = false): void => {
    wrapper.classList.add(COLLAPSED);
    teardownFullscreen();
    setChildPreview(true);
    if (returnFocus) {
      focusEl(openBtn);
    }
  };

  // The badge opens full-screen (e.g. to read history); otherwise the launcher
  // stays inline until the user sends a message.
  on(openBtn, "click", () => enterFullscreen(true));
  on(closeBtn, "click", () => collapse(true));

  on(document, "keydown", (e: Event) => {
    if ((e as KeyboardEvent).key === "Escape" && isMobileFullscreen()) {
      collapse(true);
    }
  });

  const handleViewportChange = (): void => {
    // Entered mobile mid-conversation: show the collapsed launcher, don't
    // auto-take over the screen (the CSS fullscreen rule would otherwise fire
    // as soon as the breakpoint matches).
    if (isMobile()) {
      if (
        wrapper.classList.contains(CONVERSATION) &&
        !wrapper.classList.contains(COLLAPSED)
      ) {
        collapse();
      }
      return;
    }

    // Moved to desktop: drop all mobile-only chrome (the fullscreen lock, and
    // the collapsed launcher preview which isn't locked).
    if (document.documentElement.classList.contains(PAGE_LOCK)) {
      teardownFullscreen();
    }
    wrapper.classList.remove(COLLAPSED);
    setChildPreview(false);
  };
  if (mobileQuery) {
    mobileQuery.addEventListener("change", handleViewportChange);
  }

  const handleScrollIntoView = (data: ChatMessage): void => {
    // Mobile owns the viewport: nothing to scroll inline, but a message sent
    // from the collapsed launcher re-opens it.
    if (isMobile()) {
      if (wrapper.classList.contains(COLLAPSED)) {
        enterFullscreen();
      }
      return;
    }

    if (wrapper.classList.contains(TRANSITIONING)) {
      return;
    }

    const allowed: ScrollLogicalPosition[] = ["start", "center", "end", "nearest"];
    const block =
      data.block && allowed.indexOf(data.block) >= 0 ? data.block : "end";

    const rect = iframe.getBoundingClientRect();
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    if (Math.abs(viewportH - rect.bottom) < 2) {
      return;
    }

    iframe.scrollIntoView({ block, behavior: "smooth" });
  };

  const handleStateChange = (data: ChatMessage): void => {
    const wantsConversation = data.state === "conversation";
    if (wantsConversation === wrapper.classList.contains(CONVERSATION)) {
      return;
    }

    wrapper.classList.toggle(CONVERSATION, wantsConversation);

    // Back to empty/compact: tear down the full-screen takeover.
    if (!wantsConversation) {
      wrapper.classList.remove(COLLAPSED);
      teardownFullscreen();
      setChildPreview(false);
      return;
    }

    // Restored conversation (not user-initiated): launcher on mobile, expanded
    // inline on desktop.
    if (data.initial) {
      if (isMobile()) {
        wrapper.classList.add(COLLAPSED);
        showCloseBtn(false);
        setChildPreview(true);
      }
      return;
    }

    if (isMobile()) {
      enterFullscreen();
      return;
    }

    // Desktop: grow the iframe, then scroll the input into view once settled
    // (-transitioning defers scroll-into-view).
    const reduceMotion =
      !!window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches;

    wrapper.classList.add(TRANSITIONING);
    const settle = (): void => {
      iframe.scrollIntoView({
        block: "end",
        behavior: reduceMotion ? "auto" : "smooth",
      });
      wrapper.classList.remove(TRANSITIONING);
    };
    if (reduceMotion) {
      settle();
    } else {
      window.setTimeout(settle, TRANSITION_TAIL_MS);
    }
  };

  window.addEventListener("message", (event: MessageEvent) => {
    if (event.source !== iframe.contentWindow || event.origin !== trustedOrigin) {
      return;
    }

    const data = event.data as ChatMessage | null;
    if (!data || typeof data !== "object") {
      return;
    }

    switch (data.type) {
      case "falck-chat:token-request":
        if (token) {
          postToChat({ posted_token: token });
        }
        return;
      // Empty/compact state: hug the iframe to the chat's reported composer
      // height so it isn't padded out by leftover space.
      case "falck-chat:compact-height":
        if (typeof data.height === "number" && data.height > 0 && data.height < 2000) {
          wrapper.style.setProperty(
            "--falck-chat-input-height",
            `${Math.round(data.height)}px`
          );
        }
        return;
      case "falck-chat:scroll-into-view":
        handleScrollIntoView(data);
        return;
      case "falck-chat:state":
        handleStateChange(data);
        return;
      default:
        return;
    }
  });
}

export const onContentLoaded = (): void => {
  const blocks = $$(".m-falckChatBlock");
  if (blocks) {
    blocks.forEach((block) => falckChatBlock(block));
  }
};

if (process.env.NODE_ENV !== "development") {
  document.addEventListener("DOMContentLoaded", onContentLoaded);
}
